import React, { useState, useMemo, useContext, useEffect } from "react";
import { Container, Row, Col, Card, ProgressBar } from "react-bootstrap";
import "../../assets/css/donordashboard/Payoutmanagement.css";
import MasterLayout from "../../components/layout/MasterLayout";
import axios from "axios";
import AppContext from "../../context/AppContext";

// ── localStorage helpers ───────────────────────────────────────────────────
const getPendingRewardsList = (donorId) => {
  try {
    const raw = localStorage.getItem(`rb_pending_rewards_${donorId}`);
    return raw ? JSON.parse(raw) : [];
  } catch { return []; }
};

const getClaimedRewardAmount = (donorId) => {
  try {
    const raw = localStorage.getItem(`rb_balance_${donorId}`);
    return raw ? Number(raw) : 0;
  } catch { return 0; }
};

/* ══════════════════════════════════════
   REWARD WITHDRAWAL MODAL
══════════════════════════════════════ */
function RewardWithdrawalModal({ reward, donorId, donorName, baseUrl, onClose, onSubmit }) {
  const [accountType, setAccountType] = useState("Bank Transfer");
  const [accountName, setAccountName] = useState("");
  const [bankName,    setBankName]    = useState("");
  const [walletNum,   setWalletNum]   = useState("");
  const [txnRef,      setTxnRef]      = useState("");
  const [submitting,  setSubmitting]  = useState(false);
  const [error,       setError]       = useState("");

  const isWallet = accountType !== "Bank Transfer";

  const handleSubmit = async () => {
    if (!accountName.trim()) { setError("Account name required."); return; }
    if (!isWallet && !bankName.trim()) { setError("Bank name required."); return; }
    if (isWallet && !walletNum.trim()) { setError("Wallet number required."); return; }
    if (!txnRef.trim()) { setError("Transaction reference required."); return; }
    setError("");
    setSubmitting(true);

    const data = {
      donor_id:             donorId,
      name:                 donorName,
      donations:            0,
      level:                1,
      payout:               reward.amount,
      reward_badge:         reward.name,
      accountType,
      accountName,
      bankName:             isWallet ? "" : bankName,
      walletNumber:         isWallet ? walletNum : "",
      transactionReference: txnRef,
    };

    try {
      await axios.post(`${baseUrl}/donor/withdrawals`, data);
      onSubmit({ ...data, id: Date.now(), status: "Pending", rewardId: reward.badgeId });
      onClose();
    } catch {
      setError("Submission failed. Please try again.");
      setSubmitting(false);
    }
  };

  return (
    <div className="wd-overlay" onClick={onClose}>
      <div className="wd-modal" onClick={(e) => e.stopPropagation()}>
        <div className="wd-header">
          <div>
            <div className="wd-header-title">💰 Withdraw Reward</div>
            <div className="wd-header-sub">{reward.name} — Rs. {Number(reward.amount).toLocaleString()}</div>
          </div>
          <button className="wd-close" onClick={onClose}>✕</button>
        </div>

        <div className="wd-body">
          <div className="wd-field">
            <label className="wd-label">💰 Withdrawal Amount</label>
            <div className="wd-amount-box" style={{ color: "#27ae60", borderColor: "#27ae60", background: "#e8f8f0" }}>
              Rs. {Number(reward.amount).toLocaleString()}
            </div>
          </div>

          <div className="wd-field">
            <label className="wd-label">🏦 Account Type</label>
            <select className="wd-select" value={accountType} onChange={(e) => setAccountType(e.target.value)}>
              <option>Bank Transfer</option>
              <option>Easypaisa Wallet</option>
              <option>JazzCash Wallet</option>
              <option>SadaPay Wallet</option>
            </select>
          </div>

          <div className="wd-field">
            <label className="wd-label">👤 Account Name</label>
            <input className="wd-input" type="text" placeholder="Account holder name"
              value={accountName} onChange={(e) => setAccountName(e.target.value)} />
          </div>

          <div className="wd-field">
            <label className="wd-label">{isWallet ? "📱 Wallet Number" : "🏛 Bank Name"}</label>
            {isWallet
              ? <input className="wd-input" type="text" placeholder="e.g. 03001234567"
                  value={walletNum} onChange={(e) => setWalletNum(e.target.value)} />
              : <input className="wd-input" type="text" placeholder="e.g. HBL, MCB, UBL"
                  value={bankName} onChange={(e) => setBankName(e.target.value)} />
            }
          </div>

          <div className="wd-field">
            <label className="wd-label"># Transaction Reference</label>
            <input className="wd-input" type="text" placeholder="TXN-2025-001"
              value={txnRef} onChange={(e) => setTxnRef(e.target.value)} />
          </div>

          {error && (
            <div style={{ color:"#e74c3c", fontSize:"13px", marginTop:"8px",
              padding:"8px 12px", background:"#ffeaea", borderRadius:"8px" }}>
              ⚠️ {error}
            </div>
          )}
        </div>

        <div className="wd-footer">
          <button className="wd-btn-cancel" onClick={onClose}>Cancel</button>
          <button className="wd-btn-submit" onClick={handleSubmit} disabled={submitting}>
            {submitting ? <><span className="wd-spinner" /> Processing...</> : "Submit Request"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════
   MAIN COMPONENT
══════════════════════════════════════ */
function PayoutManagement() {
  const { baseUrl, loggedInDonorId } = useContext(AppContext);

  const [toast,          setToast]          = useState(null);
  const [modalReward,    setModalReward]     = useState(null); // reward obj for modal
  const [myWithdrawals,  setMyWithdrawals]   = useState([]);
  const [donorName,      setDonorName]       = useState("My Account");
  // withdrawn reward IDs — taake button hide ho jaye
  const [withdrawnIds,   setWithdrawnIds]    = useState([]);

  const effectiveDonorId = loggedInDonorId || (() => {
    try { return JSON.parse(localStorage.getItem("loggedInDonor") || "{}").id || null; }
    catch { return null; }
  })();

  // ── load donor name + existing withdrawals ─────────────────────────────
  useEffect(() => {
    if (!effectiveDonorId) return;

    axios.get(`${baseUrl}/donor/dashboard/viewProfile/${effectiveDonorId}`)
      .then((res) => {
        const d = res.data?.profile || res.data?.donor || res.data;
        setDonorName(d?.name || d?.fullname || d?.username || "My Account");
      })
      .catch(() => {
        try {
          const s = JSON.parse(localStorage.getItem("loggedInDonor") || "{}");
          setDonorName(s?.name || s?.fullname || "My Account");
        } catch {}
      });

    axios.get(`${baseUrl}/donor/withdrawals?donor_id=${effectiveDonorId}`)
      .then((res) => {
        const all  = Array.isArray(res.data) ? res.data : (res.data.data || res.data.withdrawals || []);
        const mine = all.filter((w) => String(w.donor_id) === String(effectiveDonorId));
        setMyWithdrawals(mine.length > 0 ? mine : all);
        // jo rewards already withdraw hue hain unke badge IDs track karo
        const doneIds = (mine.length > 0 ? mine : all)
          .filter(w => w.rewardId || w.reward_badge)
          .map(w => w.rewardId || w.reward_badge);
        setWithdrawnIds(doneIds);
      })
      .catch(() => {});
  }, [effectiveDonorId]);

  // ── claimed rewards from localStorage ─────────────────────────────────
  const pendingRewardsList = getPendingRewardsList(effectiveDonorId);
  const totalClaimedAmt    = pendingRewardsList.reduce((s, r) => s + Number(r.amount), 0);

  // ── stat cards — sirf claimed rewards pe based ─────────────────────────
  const withdrawnRewardAmt  = myWithdrawals
    .filter(w => w.reward_badge || w.rewardId)
    .reduce((s, w) => s + Number(w.payout || 0), 0);
  const pendingRewardAmt    = totalClaimedAmt - withdrawnRewardAmt;
  const completedCount      = myWithdrawals.filter(w => (w.status||"").toLowerCase() === "approved").length;

  const statCards = [
    {
      cls: "payout-stat-low",
      icon: "📋",
      value: myWithdrawals.length,
      label: "Withdrawal Requests",
      sub: myWithdrawals.length > 0 ? `Rs. ${pendingRewardAmt.toLocaleString()} pending` : null,
    },
    {
      cls: "payout-stat-mid",
      icon: "✅",
      value: completedCount,
      label: "Withdrawal Completed",
      sub: `Rs. ${withdrawnRewardAmt.toLocaleString()}`,
    },
    {
      cls: "payout-stat-vip",
      icon: "💰",
      value: `Rs. ${totalClaimedAmt.toLocaleString()}`,
      label: "Total Withdrawable",
      sub: pendingRewardsList.length > 0 ? `${pendingRewardsList.length} badge reward(s)` : null,
    },
    {
      cls: "payout-stat-pending",
      icon: "⏳",
      value: `Rs. ${pendingRewardAmt.toLocaleString()}`,
      label: "Pending Payout",
      sub: null,
    },
  ];

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(null), 3500); };

  const handleModalSubmit = (data) => {
    setMyWithdrawals((prev) => [...prev, data]);
    setWithdrawnIds((prev) => [...prev, data.rewardId]);
    showToast("✓ Withdrawal request submitted!");
  };

  const paidPercent = totalClaimedAmt > 0
    ? Math.round((withdrawnRewardAmt / totalClaimedAmt) * 100)
    : 0;

  return (
    <div className="payout-page">
      <MasterLayout>
        {toast && <div className="payout-toast">{toast}</div>}

        {modalReward && (
          <RewardWithdrawalModal
            reward={modalReward}
            donorId={effectiveDonorId}
            donorName={donorName}
            baseUrl={baseUrl}
            onClose={() => setModalReward(null)}
            onSubmit={handleModalSubmit}
          />
        )}

        {/* HEADER */}
        <div className="payout-page-header">
          <Container>
            <div className="payout-header-inner">
              <div>
                <h1 className="payout-page-title">Withdrawal System</h1>
                <p className="payout-page-subtitle">Your personal payout dashboard</p>
              </div>
            </div>
          </Container>
        </div>

        <Container className="payout-content-container">

          {/* STATS */}
          <Row className="mb-4 align-items-stretch">
            {statCards.map((card, i) => (
              <Col lg={3} md={6} className="mb-3 d-flex" key={i}>
                <Card className={`payout-stat-card ${card.cls} w-100`}>
                  <Card.Body className="d-flex flex-column align-items-center justify-content-center text-center py-4">
                    <div className="payout-stat-icon">{card.icon}</div>
                    <div className="payout-stat-value"
                      style={{ fontSize: typeof card.value === "string" && card.value.length > 9 ? "1.25rem" : undefined }}>
                      {card.value}
                    </div>
                    <div className="payout-stat-label">{card.label}</div>
                    {card.sub && (
                      <div style={{ fontSize:"11px", color:"rgba(255,255,255,0.6)", marginTop:3 }}>
                        {card.sub}
                      </div>
                    )}
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>

          {/* PROGRESS */}
          <Card className="payout-progress-card mb-4">
            <Card.Body>
              <ProgressBar now={paidPercent} label={`${paidPercent}%`}
                style={{ height:"10px", borderRadius:"8px" }} />
              <div className="mt-3 d-flex flex-wrap gap-4">
                <span>✅ Paid: <strong>Rs. {withdrawnRewardAmt.toLocaleString()}</strong></span>
                <span>⏳ Pending: <strong>Rs. {pendingRewardAmt.toLocaleString()}</strong></span>
                <span>🎁 Total Claimed: <strong style={{ color:"#27ae60" }}>Rs. {totalClaimedAmt.toLocaleString()}</strong></span>
              </div>
            </Card.Body>
          </Card>

          {/* CLAIMED REWARDS TABLE — with Withdraw button */}
          {pendingRewardsList.length > 0 ? (
            <Card className="mb-4" style={{ borderRadius:"16px", overflow:"hidden", border:"2px solid #27ae60" }}>
              <Card.Body className="p-0">
                <div style={{ padding:"14px 20px", background:"linear-gradient(90deg,#27ae60,#2ecc71)", color:"#fff" }}>
                  <h5 style={{ margin:0, fontWeight:700 }}>🎁 Claimed Rewards — Ready to Withdraw</h5>
                </div>
                <table style={{ width:"100%", borderCollapse:"collapse" }}>
                  <thead>
                    <tr style={{ background:"#f0faf4" }}>
                      {["#","Reward Name","Amount","Claimed At","Status","Action"].map((h) => (
                        <th key={h} style={{
                          padding:"11px 16px", fontSize:"11px", fontWeight:700,
                          textTransform:"uppercase", color:"#27ae60", letterSpacing:"0.6px",
                          borderBottom:"2px solid #d0ecdb", textAlign:"left"
                        }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {pendingRewardsList.map((item, i) => {
                      const isWithdrawn = withdrawnIds.includes(item.badgeId) ||
                        myWithdrawals.some(w =>
                          (w.rewardId === item.badgeId) ||
                          (w.reward_badge === item.name)
                        );
                      return (
                        <tr key={i} style={{ borderBottom:"1px solid #eef8f0" }}>
                          <td style={{ padding:"12px 16px", fontSize:"13px", color:"#444", verticalAlign:"middle" }}>{i + 1}</td>
                          <td style={{ padding:"12px 16px", fontSize:"13px", fontWeight:700, color:"#1a1a1a", verticalAlign:"middle" }}>{item.name}</td>
                          <td style={{ padding:"12px 16px", fontSize:"13px", fontWeight:800, color:"#27ae60", verticalAlign:"middle" }}>
                            Rs. {Number(item.amount).toLocaleString()}
                          </td>
                          <td style={{ padding:"12px 16px", fontSize:"13px", color:"#444", verticalAlign:"middle" }}>
                            {item.claimedAt ? new Date(item.claimedAt).toLocaleDateString("en-PK") : "—"}
                          </td>
                          <td style={{ padding:"12px 16px", verticalAlign:"middle" }}>
                            {isWithdrawn ? (
                              <span style={{ padding:"3px 10px", borderRadius:"12px", fontSize:"12px",
                                fontWeight:700, background:"#d4efdf", color:"#1a6e35" }}>
                                ✓ Withdrawn
                              </span>
                            ) : (
                              <span style={{ padding:"3px 10px", borderRadius:"12px", fontSize:"12px",
                                fontWeight:700, background:"#fff3d6", color:"#b8860b" }}>
                                ⏳ Pending
                              </span>
                            )}
                          </td>
                          <td style={{ padding:"12px 16px", verticalAlign:"middle" }}>
                            {isWithdrawn ? (
                              <span style={{ color:"#27ae60", fontWeight:600, fontSize:"13px" }}>✓ Done</span>
                            ) : (
                              <button
                                onClick={() => setModalReward(item)}
                                style={{
                                  background:"linear-gradient(135deg,#27ae60,#2ecc71)",
                                  color:"#fff", border:"none", borderRadius:"8px",
                                  padding:"7px 16px", fontSize:"13px", fontWeight:700,
                                  cursor:"pointer", boxShadow:"0 2px 8px rgba(39,174,96,0.35)",
                                  transition:"opacity 0.2s",
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.opacity = "0.85"}
                                onMouseLeave={(e) => e.currentTarget.style.opacity = "1"}
                              >
                                💰 Withdraw
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </Card.Body>
            </Card>
          ) : (
            /* Agar koi reward claim nahi hua */
            <Card className="mb-4" style={{ borderRadius:"16px", border:"1.5px dashed #c8e6dc" }}>
              <Card.Body style={{ textAlign:"center", padding:"32px 20px", color:"#7a9a95" }}>
                <div style={{ fontSize:40, marginBottom:10 }}>🎁</div>
                <div style={{ fontWeight:600, fontSize:15 }}>Koi bhi reward abhi claim nahi kiya</div>
                <div style={{ fontSize:13, marginTop:6 }}>
                  Rewards & Badges page pe ja kar badge earn karo aur reward claim karo
                </div>
              </Card.Body>
            </Card>
          )}

          {/* WITHDRAWAL HISTORY — NGO ko bhi yahi data jata hai */}
          <Card className="mt-2" style={{ borderRadius:"16px", overflow:"hidden" }}>
            <Card.Body className="p-0">
              <div style={{ padding:"16px 20px", background:"#1F7A7A", color:"#fff" }}>
                <h5 style={{ margin:0, fontWeight:700 }}>📥 My Withdrawal Requests</h5>
              </div>
              <table style={{ width:"100%", borderCollapse:"collapse" }}>
                <thead>
                  <tr style={{ background:"#f4fffe" }}>
                    {["#","Reward","Account Name","Method","Bank / Wallet","Amount","Reference","Status"].map((h) => (
                      <th key={h} style={{
                        padding:"12px 16px", fontSize:"11px", fontWeight:700,
                        textTransform:"uppercase", color:"#1F7A7A", letterSpacing:"0.6px",
                        borderBottom:"2px solid #d0efec", textAlign:"left"
                      }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {myWithdrawals.length === 0 ? (
                    <tr>
                      <td colSpan={8} style={{ padding:"32px", textAlign:"center", color:"#999", fontSize:"14px" }}>
                        No withdrawal requests yet
                      </td>
                    </tr>
                  ) : myWithdrawals.map((item, i) => (
                    <tr key={i} style={{ borderBottom:"1px solid #eef8f7" }}>
                      <td style={{ padding:"12px 16px", fontSize:"13px", color:"#444", verticalAlign:"middle" }}>{i + 1}</td>
                      <td style={{ padding:"12px 16px", fontSize:"13px", fontWeight:600, color:"#27ae60", verticalAlign:"middle" }}>
                        {item.reward_badge || item.rewardId || "—"}
                      </td>
                      <td style={{ padding:"12px 16px", fontSize:"13px", color:"#333", verticalAlign:"middle" }}>
                        {item.accountName || item.account_name || "—"}
                      </td>
                      <td style={{ padding:"12px 16px", fontSize:"13px", color:"#333", verticalAlign:"middle" }}>
                        {item.accountType || item.account_type || "—"}
                      </td>
                      <td style={{ padding:"12px 16px", fontSize:"13px", color:"#333", verticalAlign:"middle" }}>
                        {item.bankName || item.bank_name || item.walletNumber || item.wallet_number || "—"}
                      </td>
                      <td style={{ padding:"12px 16px", fontSize:"13px", fontWeight:700, color:"#1F7A7A", verticalAlign:"middle" }}>
                        Rs. {Number(item.payout).toLocaleString()}
                      </td>
                      <td style={{ padding:"12px 16px", fontSize:"13px", color:"#444", verticalAlign:"middle" }}>
                        {item.transactionReference || item.transaction_reference || "—"}
                      </td>
                      <td style={{ padding:"12px 16px", verticalAlign:"middle" }}>
                        <span style={{
                          padding:"3px 10px", borderRadius:"12px", fontSize:"12px", fontWeight:700,
                          background: (item.status||"").toLowerCase() === "approved" ? "#27ae60"
                                    : (item.status||"").toLowerCase() === "rejected" ? "#e74c3c"
                                    : "#e67e22",
                          color:"#fff"
                        }}>
                          {item.status || "Pending"}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card.Body>
          </Card>

        </Container>
      </MasterLayout>
    </div>
  );
}

export default PayoutManagement;